import {
  useWSModal,
  useWSSnackbar,
  WSAlert,
  WSButton,
  WSButtons,
  WSText
} from "@wingspanhq/fe-component-library";
import { Payer } from "@wingspanhq/wingspan-api-sdk";
import { queriesPayers } from "@query";
import { ErrorAlert } from "@components/ErrorAlert";
import { ClientInfoPanel } from "../../components/ClientInfoPanel";

export interface ArchiveClientModalProps {
  client: Payer;
  onSuccess?: (client: Payer) => void;
  onClose: () => void;
}

export const ArchiveClientModal: React.FC<ArchiveClientModalProps> = ({
  client,
  onSuccess,
  onClose
}) => {
  const { openSnackbar } = useWSSnackbar();

  // v3: archive maps to the DELETE verb (PLA-3198 #11) — deletePayer deactivates
  // the payer, moving it to the archived (Deactivated) list. The registry auto-
  // invalidates the v3 payer reads and (per its config) the still-v1 payer-row
  // caches the assignment pickers read, so the archived client drops out.
  const [archiveClient, archiveClientMeta] =
    queriesPayers.deletePayer.useMutation();

  const handleArchiveClient = () => {
    archiveClient(
      { payerId: client.id as string },
      {
        onSuccess: () => {
          onSuccess?.(client);
          onClose();
          openSnackbar({
            type: "success",
            duration: 5000,
            message: "Client archived successfully"
          });
        },
        onError: () => {
          openSnackbar({
            type: "warning",
            duration: 5000,
            message: "Failed to archive client"
          });
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to archive this client?
      </WSText.ParagraphSm>

      <ClientInfoPanel client={client} mb="XL" />

      <WSAlert
        mb="XL"
        theme="info"
        icon="info-circle"
        size="M"
        title="Archiving client"
      >
        After a client is archived, no additional invoices can be created for
        this client. Existing invoices will remain on the Invoicing page until
        you delete them. You can always restore the client when needed.
      </WSAlert>

      <ErrorAlert
        error={archiveClientMeta.error}
        mb="XL"
        context="ArchiveClient"
      />

      <WSButtons format="modal">
        <WSButton
          size="M"
          name="archiveClient"
          ml="M"
          loading={archiveClientMeta.isLoading}
          onClick={handleArchiveClient}
        >
          Archive client
        </WSButton>
        <WSButton.Tertiary size="M" onClick={onClose}>
          Cancel
        </WSButton.Tertiary>
      </WSButtons>
    </>
  );
};

export const useArchiveClientModal = () =>
  useWSModal(ArchiveClientModal, {
    title: "Archive client",
    size: "M"
  });
